import type { Messages } from '@/types'

const messages: Messages = {
    common: {
        actions: {
            add: 'Añadir',
            edit: 'Editar',
            delete: 'Eliminar',
            cancel: 'Cancelar',
            back: 'Atrás',
            save: 'Guardar',
            update: 'Actualizar',
            reset: 'Restablecer',
            refresh: 'Actualizar',
            backToList: '@:common.back a la lista',
            resetDefaults: '@:common.reset a los valores predeterminados',
            confirm: 'Confirmar',
            proceed: 'Continuar',
            close: 'Cerrar',
            apply: 'Aplicar',
            search: 'Buscar',
            filter: 'Filtrar',
            clear: 'Limpiar',
            select: 'Seleccionar',
            upload: 'Subir',
            download: 'Descargar',
            view: 'Ver',
            sort: 'Ordenar',
            seeMore: 'Ver más'
        },
        status: {
            success: 'Éxito',
            error: 'Error',
            loading: 'Cargando...'
        },
        messages: {
            deleteConfirm: '¿Está seguro de que desea eliminar este usuario?',
            generalError: '¡Algo salió mal! Por favor, inténtelo de nuevo',
            addSuccess: '¡Registro agregado exitosamente!',
            updateSuccess: '¡Registro actualizado exitosamente!',
        },
        placeholder: {
            avatar: '?'
        },
        labels: {
            loading: 'Cargando',
            noResults: 'No se encontraron resultados',
            noData: 'No hay datos disponibles',
            selected: 'Seleccionado',
            required: 'Requerido',
            optional: 'Opcional',
            search: 'Buscar...',
            filter: 'Filtrar...',
            from: 'Desde',
            to: 'Hasta',
            date: 'Fecha',
            status: 'Estado',
            item: 'Elemento {index}',
            addItem: 'Agregar Elemento',
            removeItem: 'Eliminar Elemento',
            skipToMain: 'Saltar al contenido principal',
            openNavigation: 'Abrir navegación',
            closeNavigation: 'Cerrar navegación',
            mainNavigation: 'Navegación principal',
            moveUp: 'Mover Arriba',
            moveDown: 'Mover Abajo'
        },
        errors: {
            upload: 'Error al subir archivo',
            download: 'Error al descargar archivo',
            generic: 'Ocurrió un error',
            notFound: 'No encontrado',
            unauthorized: 'Acceso no autorizado',
            validation: 'Por favor, verifique su entrada'
        },
        validation: {
            required: 'Este campo es obligatorio',
            email: 'Dirección de correo electrónico no válida',
            minLength: 'La longitud mínima es {length}',
            maxLength: 'La longitud máxima es {length}',
            min: 'El valor mínimo es {min}',
            max: 'El valor máximo es {max}',
            matches: 'Formato no válido',
            unique: 'El valor debe ser único',
            confirm: 'Las contraseñas no coinciden'
        }
    },
    pages: {
        users: {
            title: 'Usuarios',
            description: 'Gestionar usuarios del sistema',
            actions: {
                invite: 'Invitar Usuario',
                resetPassword: 'Restablecer Contraseña'
            }
        },
        settings: {
            title: 'Configuración',
            actions: {
                resetDefaults: 'Restablecer a los valores predeterminados'
            }
        },
        reports: {
            title: 'Informes',
            description: 'Generar y descargar informes',
            actions: {
                generateReport: 'Generar Informe',
                downloadCsv: 'Descargar CSV',
                startDate: 'Fecha de Inicio',
                endDate: 'Fecha de Fin',
                apps: 'Aplicaciones',
            },
            error: {
                generation: 'Error al generar informe',
                download: 'Error al descargar informe'
            }
        }
    },
    navigation: {
        home: 'Inicio',
        library: 'Biblioteca',
        search: 'Buscar',
        login: 'Iniciar sesión',
        signInToGetPremium: 'Obtener Premium',
        subscribe: 'Suscribirse',
        support: 'Soporte',
        settings: 'Configuración',
        logout: 'Cerrar sesión'
    },
    settings: {
        title: 'Configuración',
        description: 'Gestiona la configuración de tu cuenta, preferencias e información del perfil.',
        sections: 'Secciones de Configuración',
        menu: {
            profile: 'Perfil',
            account: 'Cuenta',
            general: 'General',
            purchases: 'Compras',
            activity: 'Actividad'
        },
        profile: {
            title: 'Perfil',
            changeImage: 'Cambiar Imagen',
            updateProfile: 'Actualizar Perfil',
            changePassword: 'Cambiar Contraseña',
            updatePassword: 'Actualizar Contraseña',
            fields: {
                fullName: 'Nombre Completo',
                emailAddress: 'Dirección de Correo',
                dateOfBirth: 'Fecha de Nacimiento',
                currentPassword: 'Contraseña Actual',
                newPassword: 'Nueva Contraseña',
                confirmNewPassword: 'Confirmar Nueva Contraseña'
            },
            toasts: {
                imageUpdated: 'Imagen Actualizada',
                imageUpdatedMessage: 'Tu imagen de perfil ha sido actualizada exitosamente',
                uploadFailed: 'Carga Fallida',
                profileUpdated: 'Perfil Actualizado',
                profileUpdatedMessage: 'Tu perfil ha sido actualizado exitosamente',
                passwordUpdated: 'Contraseña Actualizada',
                passwordUpdatedMessage: 'Tu contraseña ha sido actualizada exitosamente',
                passwordError: 'Error de Contraseña',
                passwordMismatch: 'Las contraseñas no coinciden',
                passwordTooWeak: 'La contraseña es muy débil'
            }
        },
        account: {
            title: 'Cuenta',
            accountInformation: 'Información de la Cuenta',
            emailAddress: 'Dirección de Correo',
            memberSince: 'Miembro desde',
            redeemPromoCode: 'Canjear código de promoción',
            redeemPromoCodeDescription: '¿Tienes un código de promoción o regalo? Introdúcelo aquí para aplicarlo a tu cuenta o próxima suscripción.',
            subscription: {
                title: 'Suscripción',
                currentPlan: 'Plan Actual',
                premiumSubscription: 'Suscripción Premium',
                nextRenewal: 'Próxima renovación:',
                accessUntil: 'Acceso hasta:',
                manageInAppStore: 'Tu suscripción se compró a través de App Store. Para cancelarla o cambiarla, abre Suscripciones en la configuración de tu Apple ID en el dispositivo que usaste para suscribirte.',
                manageInPlayStore: 'Tu suscripción se compró a través de Google Play. Para cancelarla o cambiarla, abre Suscripciones en Google Play Store en el dispositivo que usaste para suscribirte.',
                notSubscribed: 'No estás suscrito actualmente a ningún plan.',
                subscribeNow: 'Suscribirse Ahora',
                findOutMore: 'Saber más',
                cancelSubscription: 'Cancelar Suscripción',
                cancelSubscriptionTitle: 'Cancelar Suscripción',
                cancelSubscriptionMessage: '¿Estás seguro de que deseas cancelar tu suscripción? Perderás el acceso al contenido premium al final de tu período de facturación actual.',
                keepSubscription: 'Mantener Suscripción',
                toasts: {
                    subscriptionActivated: 'Suscripción Activada',
                    subscriptionActivatedMessage: '¡Bienvenido a Premium!',
                    subscriptionFailed: 'Suscripción Fallida',
                    paymentMethodUpdated: 'Método de Pago Actualizado',
                    paymentMethodUpdatedMessage: 'Tu información de pago ha sido actualizada exitosamente',
                    subscriptionCancelled: 'Suscripción Cancelada',
                    subscriptionCancelledMessage: 'Tu suscripción terminará en el período de facturación actual',
                    cancellationFailed: 'Cancelación Fallida'
                }
            },
            closeAccount: {
                title: 'Cerrar Cuenta',
                requestDescription: 'Solicitar el cierre permanente de tu cuenta. Esta acción no se puede deshacer.',
                requestButton: 'Solicitar Cierre de Cuenta',
                confirmationTitle: '¿Estás seguro de que deseas desactivar tu cuenta? Esto hará:',
                confirmationItems: {
                    cancelSubscription: 'Cancelar tu suscripción activa (si la hay)',
                    removeCommunications: 'Eliminarte de todas las comunicaciones',
                    scheduleDeletion: 'Programar la eliminación permanente de tus datos después de 30 días'
                },
                confirmationWarning: 'Puedes cancelar tu solicitud de desactivación en cualquier momento dentro de 30 días. Después de este período, tu cuenta y todos los datos asociados serán eliminados permanentemente.',
                confirmationCheckbox: 'Entiendo que mi cuenta será eliminada permanentemente después de 30 días',
                deactivateAccount: 'Desactivar Cuenta',
                cancel: 'Cancelar',
                scheduledTitle: 'Desactivación de Cuenta Programada',
                scheduledMessage: 'Tu cuenta está programada para ser eliminada permanentemente el',
                scheduledWarning: 'Durante este período, no podrás iniciar sesión en tu cuenta. Si cambias de opinión, puedes cancelar el proceso de desactivación.',
                cancelDeactivation: 'Cancelar Desactivación de Cuenta',
                toasts: {
                    deactivationRequested: 'Desactivación de Cuenta Solicitada',
                    deactivationRequestedMessage: 'Tu cuenta será eliminada permanentemente en 30 días',
                    deactivationFailed: 'Desactivación Fallida',
                    deactivationCancelled: 'Desactivación Cancelada',
                    deactivationCancelledMessage: 'Tu cuenta ya no está programada para eliminación'
                }
            }
        },
        purchases: {
            title: 'Historial de Compras',
            noPurchases: 'No se encontraron compras',
            invoice: 'Factura'
        },
        activity: {
            title: 'Línea de Tiempo de Actividad',
            dateRange: 'Rango de Fechas',
            activityType: 'Tipo de Actividad',
            noActivities: 'No se encontraron actividades',
            resume: 'Reanudar',
            loadMore: 'Cargar Más',
            filters: {
                last7Days: 'Últimos 7 días',
                last30Days: 'Últimos 30 días',
                lastYear: 'Último año',
                allTime: 'Todo el tiempo',
                allActivities: 'Todas las Actividades',
                listening: 'Escuchando',
                purchases: 'Compras',
                likes: 'Me Gusta',
                bookmarks: 'Marcadores'
            },
            types: {
                listenedTo: 'Escuchado {title}',
                purchased: 'Comprado {title}',
                liked: 'Me gustó {title}',
                bookmarked: 'Marcado {title}'
            }
        },
        appSettings: {
            title: 'Configuración de la Aplicación',
            darkMode: 'Modo Oscuro',
            language: 'Idioma',
            languageOptions: {
                english: 'Inglés',
                spanish: 'Español',
                french: 'Francés',
                german: 'Alemán',
                italian: 'Italiano',
                portuguese: 'Portugués',
                dutch: 'Holandés',
                slovak: 'Eslovaco',
                russian: 'Ruso',
                arabic: 'العربية'
            }
        },
        notifications: {
            title: 'Notificaciones',
            emailNotifications: 'Notificaciones por Correo',
            emailNotificationsHint: 'Recibir actualizaciones por correo electrónico',
            pushNotifications: 'Notificaciones Push',
            pushNotificationsHint: 'Recibir notificaciones push',
            updatePreferences: 'Actualizar Preferencias'
        },
        toasts: {
            themeUpdated: 'Tema Actualizado',
            themeUpdatedMessage: 'Tu preferencia de tema ha sido guardada',
            languageUpdated: 'Idioma Actualizado',
            languageUpdatedMessage: 'Tu preferencia de idioma ha sido guardada',
            preferencesUpdated: 'Preferencias Actualizadas',
            preferencesUpdatedMessage: 'Tus preferencias de notificaciones han sido guardadas',
            updateFailed: 'Actualización Fallida',
            updateFailedMessage: 'Error al actualizar configuración',
            updateFailedNotificationMessage: 'Error al actualizar preferencias de notificaciones'
        }
    },
    auth: {
        login: {
            title: 'Iniciar Sesión',
            description: 'Inicia sesión en tu cuenta de Heard para acceder a tu biblioteca y contenido premium',
            signIn: 'Iniciar Sesión',
            signInToAccount: 'Inicia sesión en tu cuenta',
            noAccount: '¿No tienes una cuenta?',
            signUp: 'Regístrate',
            continueAsGuest: 'Continuar como invitado',
            or: 'O',
            agreeToTerms: 'Al iniciar sesión, aceptas nuestros',
            termsOfService: 'Términos de Servicio',
            privacyPolicy: 'Política de Privacidad',
            forgotPassword: '¿Olvidaste tu contraseña?'
        },
        register: {
            title: 'Registrarse',
            description: 'Crea una nueva cuenta de Heard para acceder a tu biblioteca y contenido premium',
            createAccount: 'Crear Cuenta',
            createYourAccount: 'Crea tu cuenta',
            orSignIn: 'O',
            signIn: 'iniciar sesión',
            backToHome: 'Volver a la página de inicio',
            agreeToTerms: 'Al crear una cuenta, aceptas nuestros',
            termsOfService: 'Términos de Servicio',
            privacyPolicy: 'Política de Privacidad'
        },
        forms: {
            email: 'Correo Electrónico',
            password: 'Contraseña',
            confirmPassword: 'Confirmar Contraseña',
            fullName: 'Nombre Completo',
            rememberMe: 'Recordarme',
            signIn: 'Iniciar Sesión',
            createAccount: 'Crear Cuenta'
        },
    },
    player: {
        chapters: 'Capítulos',
        options: 'Opciones',
        playbackSpeed: 'Velocidad de Reproducción',
        share: 'Compartir',
        linkCopied: 'Enlace Copiado',
        linkCopiedMessage: '¡Enlace compartido copiado al portapapeles!',
        shareError: 'Error al Compartir',
        shareErrorMessage: 'No se pudo copiar el enlace. Por favor, inténtelo de nuevo.',
        bookmarkAdded: 'Éxito',
        bookmarkAddedMessage: '¡Marcador agregado!',
        jumpedToBookmark: 'Éxito',
        jumpedToBookmarkMessage: '¡Saltar al marcador!',
        likeError: 'Error al Me Gusta',
        likeErrorMessage: 'No se pudo actualizar el estado de me gusta. Por favor, inténtelo de nuevo.',
        bookmarkError: 'Error al Marcador',
        bookmarkErrorMessage: 'No se pudo actualizar el marcador. Por favor, inténtelo de nuevo.',
        favorites: {
            add: 'Agregar a Favoritos',
            remove: 'Eliminar de Favoritos'
        },
        unknownChapter: 'Capítulo Desconocido',
        unknownTitle: 'Título Desconocido',
        unknownAuthor: 'Autor Desconocido'
    },
    details: {
        starring: 'Protagonistas:',
        author: 'Autor',
        narrator: 'Narrador',
        pause: 'Pausar',
        purchased: 'Comprado',
        continueReading: 'Continuar Leyendo',
        continuePlaying: 'Continuar Reproduciendo',
        play: 'Reproducir',
        loading: 'Cargando...',
        noChapters: 'No hay capítulos disponibles',
        chapterFreeLabel: 'Gratis',
        listen: 'Escuchar',
        purchaseSuccess: 'Compra exitosa',
        purchaseSuccessMessage: '{title} ha sido añadido a tu biblioteca',
        subscriptionSuccess: 'Suscripción exitosa',
        subscriptionSuccessMessage: '¡Bienvenido a Heard Premium!',
        subscriptionFailed: 'Suscripción fallida',
        subscriptionErrorMessage: 'Hubo un error al procesar tu suscripción.',
        info: 'Info',
        more: 'Más',
        episodes: 'Episodios',
        about: 'Acerca de',
        series: 'Serie',
        producedBy: 'Producido por',
        recommendations: 'También te puede gustar',
        moreFromCategory: 'Más de {name}',
        moreFromAuthor: 'Más de {name}',
        moreFromCast: 'Más de {name}',
        moreFromSeries: 'Más en {name}',
        noRelated: 'No hay títulos relacionados disponibles por ahora.',
        ageVerification: {
            title: 'Solo para adultos',
            message: 'Este contenido es solo para adultos. Confirma que quieres continuar.',
            minAgeHint: 'Debes tener al menos {age} años para ver este título.',
            confirm: 'Soy adulto, continuar',
            cancel: 'Cancelar',
            missingDobTitle: 'Fecha de nacimiento requerida',
            missingDobMessage: 'Añade tu fecha de nacimiento en tu perfil para ver este contenido.',
            underageTitle: 'Restringido por edad',
            underageMessage: 'Debes tener al menos {age} años para ver este contenido.',
        },
    },
    shareModal: {
        title: 'Compartir',
        copyLink: 'Copiar enlace',
        copyLinkWithMessage: 'Copiar enlace con mensaje',
        copyLinkWithMessageHint: 'Incluye título y descripción',
        shareVia: 'Compartir mediante...',
        shareViaHint: 'Apps, mensajes, correo',
        linkCopied: 'Enlace copiado',
        linkCopiedMessage: '¡Enlace copiado al portapapeles!',
        shared: 'Compartido',
        sharedMessage: 'Contenido compartido correctamente'
    },
    contextMenu: {
        playFromBeginning: 'Reproducir desde el Principio',
        markAsFinished: 'Marcar como Finalizado',
        addToFavorites: 'Agregar a Favoritos',
        removeFromFavorites: 'Eliminar de Favoritos',
        share: 'Compartir',
        viewDetails: 'Ver Detalles',
        removeFromLibrary: 'Eliminar de la Biblioteca',
        continuePlaying: 'Continuar Reproduciendo',
        continueReading: 'Continuar Leyendo'
    },
    actions: {
        subscribe: 'Suscribirse',
        signInToGetPremium: 'Obtener Premium',
        purchase: 'Comprar',
        buy: 'Comprar',
        buyFor: 'Comprar por {price}',
        purchasedGoToLibrary: 'Comprado - Ir a la Biblioteca',
        addToFavorites: 'Agregar a favoritos',
        removeFromFavorites: 'Eliminar de favoritos',
        share: 'Compartir'
    },
    browse: {
        sortBy: 'Ordenar por',
        sortOptions: {
            recent: 'Recientes',
            ascending: 'A-Z',
            descending: 'Z-A'
        }
    },
    cookieConsent: {
        title: 'Cookies',
        message: 'Utilizamos cookies para mejorar su experiencia de navegación y analizar nuestro tráfico. Al hacer clic en "Aceptar", consiente nuestro uso de cookies. Más información en nuestra',
        privacyPolicy: 'Política de Privacidad',
        accept: 'Aceptar',
        reject: 'Rechazar'
    },

    legal: {
        companyNumber: 'N.º de empresa',
        privacyPolicy: 'Política de privacidad',
        termsOfService: 'Términos de servicio',
        agreeToTermsSubscribe: 'Al suscribirte, aceptas nuestros',
        operatedBy: '{appName} está operado por {companyName}, {address}',
        faqs: 'Preguntas frecuentes'
    },
    support: {
        title: 'Soporte',
        description: '¿Necesita ayuda con su cuenta, suscripción o experiencia de escucha? Consulte nuestras preguntas frecuentes e información legal, o envíenos un mensaje.',
        intro: '¿Necesita ayuda con su cuenta, suscripción o experiencia de escucha? Consulte nuestras preguntas frecuentes e información legal a continuación, o contáctenos si necesita más ayuda.',
        quickLinks: 'Enlaces rápidos',
        contactTitle: 'Contactar soporte',
        contactDescription: 'Envíe un mensaje a nuestro equipo de soporte. Consulte primero las preguntas frecuentes para respuestas rápidas.',
        contactIntro: 'Si necesita ayuda, estamos aquí para usted. Muchas preguntas comunes ya están respondidas en nuestro centro de ayuda.',
        contactIntroFaqBefore: 'Consulte nuestras ',
        contactIntroFaqAfter: ' para respuestas a preguntas comunes. Si aún necesita ayuda, rellene el formulario y le responderemos lo antes posible.',
        contactSubmit: 'Enviar mensaje',
        contactName: 'Nombre',
        contactEmail: 'Correo electrónico',
        contactSubject: 'Asunto',
        contactMessage: 'Mensaje',
        contactMessageHint: 'Describa su problema con detalle',
        backToSupport: 'Volver al soporte',
        pageLoading: 'Cargando…',
        pageNotAvailable: 'Esta página no está disponible en este momento.',
        unsupportedSection: 'Sección de contenido no compatible: {name}',
        useContactFormBefore: '¿Necesita ponerse en contacto? Use nuestro ',
        contactSuccessTitle: 'Mensaje enviado',
        contactSuccessMessage: 'Su mensaje ha sido enviado. Le responderemos lo antes posible.',
        contactErrorTitle: 'Error al enviar',
        contactErrorMessage: 'No se pudo enviar su mensaje. Por favor, inténtelo de nuevo.'
    },
    promo: {
        haveCode: '¿Tienes un código de promoción?',
        placeholder: 'Introduce el código',
        apply: 'Aplicar',
        remove: 'Quitar',
        applied: '{name} – {discount}',
        invalidCode: 'Código no válido o caducado.',
        expired: 'Este código ha caducado.',
        notValidForPurchase: 'Este código no es válido para esta compra.',
        free: 'Gratis',
        percentOff: '{value}% de descuento',
        fixedOff: '{value} de descuento',
        useWhenSubscribe: 'Usa esta oferta cuando te suscribas a continuación.',
        useWhenPurchase: 'Usa este código al comprar un título.',
        accessGranted: 'El acceso se ha aplicado a tu cuenta.'
    }
}

export default messages
